import React, { useMemo, useState, useEffect } from 'react';
import {
    useReactTable,
    createColumnHelper,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    getFilteredRowModel,
    flexRender, SortingState
} from '@tanstack/react-table';

import api from '../../config/axios';
import AdminMenu from '../../components/admin/menu';

interface TEpreuve {
    epreuveId: number;
    epreuveLib: string;
    valueLink: string;
}

const AdminEpreuve: React.FC = () => {
    const [data, setData] = useState<TEpreuve[]>([]);
    const [globalFilter, setGlobalFilter] = useState('');
    const [sorting, setSorting] = useState<SortingState>([]);

    const handleDelete = async (id: number) => {
        if (window.confirm('Êtes-vous sûr de vouloir supprimer cette épreuve ?')) {
            try {
                await api.delete(`/epreuve/${id}`);
                setData(data.filter(epreuve => epreuve.epreuveId !== id));
            } catch (error) {
                console.error('Erreur lors de la suppression:', error);
            }
        }
    };

    useEffect(() => {
        const fetchEpreuves = async () => {
            try {
                const response = await api.get('/epreuve');
                setData(response.data);
            } catch (error) {
                console.error('Erreur lors du chargement des épreuves:', error);
            }
        };
        fetchEpreuves();
    }, []);

    const columnHelper = createColumnHelper<TEpreuve>();
    const columns = useMemo(
        () => [
            columnHelper.accessor('epreuveId', {
                header: () => 'ID',
                cell: info => info.getValue(),
            }),
            columnHelper.accessor('epreuveLib', {
                header: () => 'Nom',
                cell: info => info.getValue(),
            }),
            columnHelper.accessor('valueLink', {
                header: () => 'Value Link',
                cell: info => info.getValue(),
            }),
            columnHelper.display({
                id: 'actions',
                header: () => 'Actions',
                cell: ({ row }) => (
                    <div className="flex space-x-2">
                        <button
                            onClick={() => alert(`Modifier ID: ${row.original.epreuveId}`)}
                            className="bg-yellow-500 text-white px-2 py-1 rounded-md hover:bg-yellow-400"
                        >
                            Modifier
                        </button>
                        <button
                            onClick={() => handleDelete(row.original.epreuveId)}
                            className="bg-red-600 text-white px-2 py-1 rounded-md hover:bg-red-500"
                        >
                            Supprimer
                        </button>
                    </div>
                ),
            }),
        ],
        []
    );

    const table = useReactTable({
        data,
        columns,
        state: {
            globalFilter,
            sorting,
        },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        initialState: {
            pagination: {
                pageSize: 10,
            },
        },
    });

    return (
        <>
            <div className="flex h-screen bg-gray-100">
                <AdminMenu />
                <main className="flex-1 p-6 bg-white shadow-md rounded-md">
                    <h1 className="text-2xl font-bold mb-4">Gestion des Épreuves</h1>
                    {/* Filtrage global */}
                    <input
                        type="text"
                        value={globalFilter || ''}
                        onChange={(e) => setGlobalFilter(e.target.value)}
                        placeholder="Rechercher..."
                        className="mb-4 p-2 border rounded-md"
                    />

                    {/* Table */}
                    <table className="table-auto w-full border-collapse border border-gray-200">
                        <thead>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <tr key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => (
                                        <th key={header.id} className="border px-4 py-2 bg-gray-100">
                                            {flexRender(header.column.columnDef.header, header.getContext())}
                                            {/* Ajout de tri */}
                                            {header.column.getCanSort() && (
                                                <button
                                                    onClick={header.column.getToggleSortingHandler()}
                                                    className="ml-2 text-blue-500"
                                                >
                                                    {header.column.getIsSorted() === 'asc' ? ' 🔼' : header.column.getIsSorted() === 'desc' ? ' 🔽' : '⇅'}
                                                </button>
                                            )}
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </thead>
                        <tbody>
                            {table.getRowModel().rows.map((row) => (
                                <tr key={row.id}>
                                    {row.getVisibleCells().map((cell) => (
                                        <td key={cell.id} className="border px-4 py-2">
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            {table.getFooterGroups().map((footerGroup) => (
                                <tr key={footerGroup.id}>
                                    {footerGroup.headers.map((header) => (
                                        <td key={header.id} className="border px-4 py-2">
                                            {flexRender(header.column.columnDef.footer, header.getContext())}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tfoot>
                    </table>

                    {/* Pagination */}
                    <div className="flex items-center justify-between mt-4">
                        <button
                            onClick={() => table.previousPage()}
                            disabled={!table.getCanPreviousPage()}
                            className="bg-gray-200 px-4 py-2 rounded-md disabled:opacity-50"
                        >
                            Précédent
                        </button>
                        <span>
                            Page{' '}
                            <strong>
                                {table.getState().pagination.pageIndex + 1} sur {table.getPageCount()}
                            </strong>
                        </span>
                        <button
                            onClick={() => table.nextPage()}
                            disabled={!table.getCanNextPage()}
                            className="bg-gray-200 px-4 py-2 rounded-md disabled:opacity-50"
                        >
                            Suivant
                        </button>
                    </div>
                </main>
            </div>
        </>
    );
};

export default AdminEpreuve;